// Feature: application-surface-reconciliation
//
// Pure nav-consolidation decision module (Layer 2 — Admin_Navigation).
//
// This is the single source of truth for *which* admin navigation entries are
// visible in each flag state and *which* Director_Task each entry resolves to.
// It is intentionally pure logic with no React / icon / I/O dependencies so the
// nine correctness properties can test the nav config directly rather than
// rendered pixels. The `AdminSidebar.tsx` consolidation (tasks 6.1 / 6.2)
// consumes these selectors so the sidebar group, the `PillTabs` strip, and the
// nav can never disagree.
//
// The invariants this module enforces (design Properties 4 & 9):
//   - The standalone "Submissions" entry is ABSENT in every flag state, so the
//     Application_Review task is reachable only through Approvals → Applications
//     (Req 6.3).
//   - Each Director_Task maps to AT MOST ONE visible entry (Req 6.1).
//   - Costume / Stage Form / Interview Info / Placement Questions approval
//     display no longer routes through the builder ("Forms") surface: the
//     approval-rollup-surfaces feature supersedes that fold-into-Forms model,
//     surfacing each approval-requiring page as its own derived read-only
//     Rollup_Tab instead (Req 7.2, 7.7, superseding Req 6.4).
//   - Every visible entry carries the capability its destination route requires,
//     so a visible entry never links to a route the role cannot open (Req 6.5).

import type { Capability } from '@/lib/accessCapabilities';
import { computeCapabilities, type AccessHat } from '@/lib/accessCapabilities';
import type { RoleType } from '@/context/AuthContext';
import type { FormPage } from '@/lib/application-form/client/types';
import { approvalActionPages } from '@/lib/application-form/reconciliation/approvalPages';

// ---------------------------------------------------------------------------
// Director_Task domain
// ---------------------------------------------------------------------------
//
// NOTE: the canonical `Reconciled_Tasks` union is owned by `ssotMap.ts`
// (task 1.1, being authored in parallel). Until that module lands, this union
// is defined here against the design's task vocabulary verbatim so the names
// align; the wiring tasks (6.x) reconcile the two into a single exported type.

/**
 * A real-world admin task that exists as both a legacy and a new surface
 * (design glossary: Director_Task). Names match the design document exactly.
 */
export type DirectorTask =
  | 'Application_Review'
  | 'Title_Approval'
  | 'Costume_Approval'
  | 'Stage_Form'
  | 'Interview_Info'
  | 'Placement_Questions'
  | 'Document_Management';

// ---------------------------------------------------------------------------
// Capability mapping (mirrors AdminSidebar.NAV_ITEM_CAPABILITY)
// ---------------------------------------------------------------------------
//
// The reconciliation-owned nav entries ("Approvals" group + its sub-tabs, and
// the builder "Forms" entry) are all `canManageUsers`-gated, exactly as in
// `AdminSidebar.NAV_ITEM_CAPABILITY`. Kept here (rather than imported from the
// sidebar) so this stays a dependency-free pure module; task 6.2 wires the
// sidebar to consume these selectors, keeping the two in sync.

/** Required capability per reconciliation nav entry name. */
export const NAV_ITEM_CAPABILITY: Readonly<Record<string, Capability>> = {
  Approvals: 'canManageUsers',
  Applications: 'canManageUsers',
  Titles: 'canManageUsers',
  'Approved Titles': 'canManageUsers',
  Costumes: 'canManageUsers',
  Forms: 'canManageUsers',
};

// ---------------------------------------------------------------------------
// Approvals sub-page selector (flag-aware)
// ---------------------------------------------------------------------------

/** Canonical Approvals sub-tab ids consumed by `useSearchParams.get('tab')`. */
export type ApprovalsSubpageId = 'applications' | 'titles' | 'approved-titles' | 'costumes';

/** A flag-aware Approvals sub-page descriptor (icon-free; the sidebar adds icons). */
export interface ApprovalsSubpage {
  /** Visible label. */
  readonly name: string;
  /** Tab id for `useSearchParams.get('tab')` matching. */
  readonly tab: ApprovalsSubpageId;
  /** Destination route. */
  readonly path: string;
  /** Capability required to open the destination route. */
  readonly capability: Capability;
  /** The Director_Task(s) this sub-page resolves to. */
  readonly tasks: readonly DirectorTask[];
}

const APPLICATIONS_SUBPAGE_OFF: ApprovalsSubpage = {
  name: 'Applications',
  tab: 'applications',
  path: '/admin/approvals?tab=applications',
  capability: NAV_ITEM_CAPABILITY.Applications,
  // Flag-off the Applications tab is the legacy whole-application review only.
  tasks: ['Application_Review'],
};

const APPLICATIONS_SUBPAGE_ON: ApprovalsSubpage = {
  name: 'Applications',
  tab: 'applications',
  path: '/admin/approvals?tab=applications',
  capability: NAV_ITEM_CAPABILITY.Applications,
  // Flag-on the Applications tab renders the new review surface (the applicant
  // roster + per-applicant review). Title_Approval is served by its OWN
  // dedicated `Approved Titles` subpage (see `APPROVED_TITLES_SUBPAGE`), so the
  // Applications entry resolves to Application_Review ONLY — keeping the
  // one-entry-per-task invariant (Property 4) intact now that the
  // Approved_Titles_View lives on its own tab (season-scoped-title-registry
  // §6, Req 12.24).
  tasks: ['Application_Review'],
};

/**
 * The dedicated "Approved Titles" Approvals subpage (season-scoped-title-
 * registry §6, Req 12.20/12.23/12.24). Present flag-on only — it hosts the
 * relocated Approved_Titles_View (approved roll-up + pending queue + inline
 * approve/promotion). It REUSES the existing `Title_Approval` Director_Task
 * (no new task in the union): this subpage IS the surface where a Director
 * resolves the title-approval task, so `APPLICATIONS_SUBPAGE_ON.tasks` drops
 * `Title_Approval` above and it resolves to exactly this one visible entry.
 */
const APPROVED_TITLES_SUBPAGE: ApprovalsSubpage = {
  // User-visible label is "Titles" (season-scoped-title-registry follow-up).
  // The tab id (`approved-titles`), route, capability, and Title_Approval task
  // mapping are deliberately unchanged — only the display string differs, so
  // the one-entry-per-task invariant and existing deep links are preserved.
  name: 'Titles',
  tab: 'approved-titles',
  path: '/admin/approvals?tab=approved-titles',
  capability: NAV_ITEM_CAPABILITY['Approved Titles'],
  tasks: ['Title_Approval'],
};

const COSTUMES_SUBPAGE: ApprovalsSubpage = {
  name: 'Costumes',
  tab: 'costumes',
  path: '/admin/approvals?tab=costumes',
  capability: NAV_ITEM_CAPABILITY.Costumes,
  tasks: ['Costume_Approval'],
};

/**
 * The flag-aware Approvals sub-tab set (design §AdminSidebar seam).
 *
 *   - Flag-off: `[Applications, Costumes]` — the legacy standalone "Titles"
 *     tab has been retired (season-scoped-title-registry): title management now
 *     lives on the admin Settings page as the season-scoped `TitleManagement`
 *     surface, and approved titles are read from the SSOT-based Approved Titles
 *     view. The legacy `ApplicantYearProfile`-backed `TitleRegistry` no longer
 *     appears as an Approvals sub-tab (Req 4.5, 8.1, 11.5).
 *   - Flag-on:  `[Applications, Approved Titles]` — the Applications tab renders
 *     the new review surface (Application_Review), and the relocated
 *     Approved_Titles_View lives on its own dedicated "Approved Titles" subpage
 *     serving Title_Approval (season-scoped-title-registry §6, Req 12.20).
 *     Costume / Stage Form / Interview Info / Placement Questions move into the
 *     builder ("Forms") as FormPages (Req 6.4, 7).
 */
export function approvalsSubpages(flag: boolean): readonly ApprovalsSubpage[] {
  return flag
    ? [APPLICATIONS_SUBPAGE_ON, APPROVED_TITLES_SUBPAGE]
    : [APPLICATIONS_SUBPAGE_OFF, COSTUMES_SUBPAGE];
}

// ---------------------------------------------------------------------------
// Nav_Deriver — config-driven Approvals tab derivation (approval-rollup-surfaces)
// ---------------------------------------------------------------------------
//
// Feature: approval-rollup-surfaces
//
// This section SUPERSEDES the fixed `approvalsSubpages(flag)` set above: instead
// of a hardcoded sub-tab list, the Approvals navigation DERIVES exactly one
// read-only Rollup_Tab per approval-requiring `FormPage` on the Active_Form
// (design §Component 1). The two selectors here are pure so the correctness
// properties test the derivation directly rather than rendered pixels; the
// wiring tasks (7.x) switch `AdminSidebar.tsx` / `SettingsPage.tsx` off the
// fixed set and onto `visibleApprovalsTabs`.

/**
 * A single Approvals navigation entry — either the constant, retained
 * Applications entry (the Discrete_Review, `derived: false`) or a derived
 * read-only Rollup_Tab surfacing one approval-requiring `FormPage`
 * (`derived: true`). Every entry is `canManageUsers`-gated (Req 1.7, 7.5).
 */
export interface ApprovalsEntry {
  /** The FormPage this entry surfaces; null for the constant Applications entry. */
  readonly formPageId: string | null;
  /** URL tab id for `useSearchParams.get('tab')` — the page's pageKey (id fallback). */
  readonly tab: string;
  /** Visible label. */
  readonly label: string;
  /** Destination route (`/admin/approvals?tab=<tab>`). */
  readonly path: string;
  /** Capability required to open the entry — always `canManageUsers`. */
  readonly capability: Capability;
  /** True for a derived Rollup_Tab, false for the constant retained entry. */
  readonly derived: boolean;
}

/**
 * A single derived read-only Approvals sub-tab for one approval-requiring
 * FormPage. A narrowed `ApprovalsEntry` (non-null `formPageId`, `derived: true`).
 */
export interface RollupTab extends ApprovalsEntry {
  /** The FormPage this tab surfaces (the SK component for PageApproval reads). */
  readonly formPageId: string;
  readonly derived: true;
}

/** The capability every Approvals entry is gated on (Req 1.7, 7.5). */
const APPROVALS_CAPABILITY: Capability = NAV_ITEM_CAPABILITY.Approvals;

/**
 * The constant, retained Applications entry (the Discrete_Review). Present in
 * every flag state per Req 7.6 — it is NOT a derived roll-up and never depends
 * on the Active_Form's pages.
 */
const APPLICATIONS_ENTRY: ApprovalsEntry = {
  formPageId: null,
  tab: 'applications',
  label: 'Applications',
  path: '/admin/approvals?tab=applications',
  capability: APPROVALS_CAPABILITY,
  derived: false,
};

/** True when `value` is null/undefined or whitespace-only (Req 1.10, 1.4). */
function isBlank(value: string | null | undefined): boolean {
  return value == null || value.trim().length === 0;
}

/**
 * The stable ordering / label-fallback key for a FormPage: its human-authored
 * `pageKey` when present and non-empty, otherwise the persisted, unique `id`
 * (Design Decision D1 — legacy pages have no `pageKey`).
 */
function pageKeyOrId(page: FormPage): string {
  const key = page.pageKey;
  if (typeof key === 'string' && key.length > 0) return key;
  return page.id;
}

/**
 * The derived, ordered Rollup_Tab set for the Active_Form's approval pages
 * (Req 1.1–1.6, 1.10). Pure: the input is never mutated.
 *
 *   - Filters to approval-requiring pages via the existing
 *     `approvalActionPages` selector (Req 1.1, 1.2), so a page with
 *     `requiresDirectorApproval === false` is excluded and an input with no
 *     approval page yields an empty set (Req 1.6).
 *   - Sorts a COPY by `displayOrder` ascending, breaking ties by `pageKey`
 *     ascending lexicographically with an `id` fallback, so the order is
 *     deterministic regardless of input order (Req 1.3).
 *   - Labels each tab with the page `title` when non-blank, else its `pageKey`
 *     (id fallback) (Req 1.4, 1.10).
 *   - Uses only the pages as input, so the result is organization-independent
 *     (Req 1.5).
 */
export function deriveRollupTabs(pages: readonly FormPage[]): RollupTab[] {
  const approval = approvalActionPages(pages);
  const sorted = [...approval].sort((a, b) => {
    if (a.displayOrder !== b.displayOrder) {
      return a.displayOrder - b.displayOrder;
    }
    const aKey = pageKeyOrId(a);
    const bKey = pageKeyOrId(b);
    if (aKey < bKey) return -1;
    if (aKey > bKey) return 1;
    return 0;
  });

  return sorted.map((page) => {
    const tab = pageKeyOrId(page);
    const label = isBlank(page.title) ? tab : page.title;
    return {
      formPageId: page.id,
      tab,
      label,
      path: `/admin/approvals?tab=${tab}`,
      capability: APPROVALS_CAPABILITY,
      derived: true,
    };
  });
}

/**
 * The flag- and capability-gated Approvals tab set (Req 1.7, 5.1, 5.2, 5.5,
 * 7.1, 7.6). Pure.
 *
 * Always returns the constant, retained Applications entry (Req 7.6). It appends
 * the derived Rollup_Tabs ONLY when the Application_Form_Flag is enabled AND the
 * current user holds `canManageUsers`; if either condition is unmet (flag off or
 * capability absent) it returns only the Applications entry and no derived tab
 * (Req 1.7, 5.1, 5.5, 7.6). Every derived tab it returns carries exactly the
 * `canManageUsers` capability (Req 7.5).
 */
export function visibleApprovalsTabs(
  flag: boolean,
  pages: readonly FormPage[],
  hasCapability: (c: Capability) => boolean,
): readonly ApprovalsEntry[] {
  const entries: ApprovalsEntry[] = [APPLICATIONS_ENTRY];
  if (flag && hasCapability(APPROVALS_CAPABILITY)) {
    entries.push(...deriveRollupTabs(pages));
  }
  return entries;
}

// ---------------------------------------------------------------------------
// Consolidated nav-entry set (flag- and role-aware)
// ---------------------------------------------------------------------------

/** A single visible navigation entry that resolves to one or more Director_Tasks. */
export interface NavEntry {
  /** Visible label. */
  readonly name: string;
  /** Destination route. */
  readonly path: string;
  /** Capability required to open the destination route (Req 6.5). */
  readonly capability: Capability;
  /** The Director_Task(s) this entry resolves to. */
  readonly tasks: readonly DirectorTask[];
}

/**
 * The builder "Forms" entry. Present only flag-on, where it is the single nav
 * home for the application-form authoring/editing surface
 * (`/admin/application-builder`). Capability mirrors `NAV_ITEM_CAPABILITY.Forms`.
 *
 * approval-rollup-surfaces supersedes the fold-into-Forms approval-display model
 * (Req 7.2, 7.7): the Costume / Stage Form / Interview Info / Placement Questions
 * approval displays no longer route through the builder "Forms" surface — each
 * approval-requiring page now surfaces SOLELY as its own derived read-only
 * Rollup_Tab (`deriveRollupTabs` / `visibleApprovalsTabs`). The "Forms" entry
 * therefore resolves NO approval Director_Task; it remains only the builder home
 * for form-page authoring/editing, which is left unchanged (Req 7.8).
 */
const FORMS_ENTRY: NavEntry = {
  name: 'Forms',
  path: '/admin/application-builder',
  capability: NAV_ITEM_CAPABILITY.Forms,
  tasks: [],
};

/** Map a web `RoleType` to the capabilities it holds (single-hat projection). */
const ROLE_TO_HAT: Partial<Record<RoleType, AccessHat>> = {
  admin: 'admin',
  poweruser: 'poweruser',
  judge: 'judge',
  // `contestant` holds no admin Access_Hat — it confers Portal_Access only.
};

/**
 * The capabilities a `RoleType` confers, derived through the canonical
 * `computeCapabilities` so it matches the production AuthContext derivation.
 */
export function roleCapabilities(role: RoleType): ReadonlySet<Capability> {
  const roleSet = new Set<AccessHat>();
  const hat = ROLE_TO_HAT[role];
  if (hat) roleSet.add(hat);
  return computeCapabilities({ roleSet, portalAccess: false });
}

/**
 * The consolidated, flag- and role-aware set of reconciliation nav entries.
 *
 * Construction:
 *   1. Start from the flag-aware Approvals sub-tabs (`approvalsSubpages`), each
 *      of which is a navigable, task-resolving leaf entry.
 *   2. Append the builder "Forms" entry (authoring/editing home only) when the
 *      flag is on. It resolves no approval Director_Task — approval-rollup-
 *      surfaces removed the fold-into-Forms approval-display routing (Req 7.7).
 *   3. The standalone "Submissions" entry is NEVER included (Req 6.3) — it is
 *      simply absent from this construction in both flag states.
 *   4. Filter to entries whose required capability the role holds (Req 6.5), so
 *      every returned entry resolves to a route the role can open.
 *
 * The result satisfies the design invariants: at most one entry per
 * Director_Task (Property 4) and every visible entry is permitted (Property 9).
 */
export function visibleNavEntries(flag: boolean, role: RoleType): readonly NavEntry[] {
  const candidates: NavEntry[] = approvalsSubpages(flag).map((sub) => ({
    name: sub.name,
    path: sub.path,
    capability: sub.capability,
    tasks: sub.tasks,
  }));

  if (flag) {
    candidates.push(FORMS_ENTRY);
  }

  const caps = roleCapabilities(role);
  return candidates.filter((entry) => caps.has(entry.capability));
}
